import {
  getCreatorAccountPickerOwners,
  getCreatorAccountPickerPlatforms,
  getCreatorAccountSelectionState,
  getCreatorQuoteSelectablePlacementIds,
  toggleCreatorAccountPlacementIds,
  toggleCreatorAccountPlatformPlacementIds,
  type CreatorAccountPickerAccount,
  type CreatorAccountPickerOwner,
  type CreatorAccountPickerPlatform
} from '@/biz/multiPlatformCollaboration/creatorAccountPicker';
import type { CreatorCollaborationPackage } from '@/biz/multiPlatformCollaboration/model';
import { getPlatformName } from '@/biz/platform';
import {
  ArrowUpRightIcon,
  CheckMarkIcon,
  ChevronStrokeIcon,
  MinusMarkIcon,
  PlusMarkIcon
} from '@/components/common/icon';
import CreatorAddAccountsButton from './CreatorAddAccountsButton';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const MENU_SHADOW =
  'shadow-[0_12px_16px_-4px_rgba(0,0,0,0.08),0_4px_6px_-2px_rgba(0,0,0,0.03),0_2px_2px_-1px_rgba(0,0,0,0.04)]';

function MenuChevron({ backwards = false }: { backwards?: boolean }) {
  return (
    <span
      aria-hidden
      className={`flex h-4 w-4 flex-shrink-0 items-center justify-center ${
        backwards ? 'rotate-180' : ''
      }`}
    >
      <ChevronStrokeIcon className="h-[9.5px] w-[5.5px] text-[#A3A3A3]" />
    </span>
  );
}

function VerifyArrow() {
  return (
    <span
      aria-hidden
      className="flex h-4 w-4 flex-shrink-0 items-center justify-center"
    >
      <ArrowUpRightIcon className="h-[8.1667px] w-[8.1667px] text-[#FF7728]" />
    </span>
  );
}

function AddIcon() {
  return (
    <span
      aria-hidden
      className="flex h-4 w-4 flex-shrink-0 items-center justify-center"
    >
      <PlusMarkIcon className="h-[10.8333px] w-[10.8333px] text-[#FF7728]" />
    </span>
  );
}

function CheckMark({
  checked,
  indeterminate
}: {
  checked: boolean;
  indeterminate: boolean;
}) {
  return (
    <span
      aria-hidden
      className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
        checked || indeterminate
          ? 'border-[#FF621D] bg-[#FF621D]'
          : 'border-[#D4D4D4] bg-white'
      }`}
    >
      {checked || indeterminate ? (
        indeterminate ? (
          <MinusMarkIcon className="h-[1.6667px] w-[8.6667px] text-white" />
        ) : (
          <CheckMarkIcon className="h-[7.1667px] w-[9.6667px] text-white" />
        )
      ) : null}
    </span>
  );
}

function AccountAvatar({
  account,
  owner
}: {
  account: CreatorAccountPickerAccount;
  owner: CreatorAccountPickerOwner;
}) {
  const avatar = account.avatar || owner.avatar;
  const name = account.userName || owner.name;
  if (avatar) {
    return (
      <img
        src={avatar}
        alt=""
        className="h-5 w-5 flex-shrink-0 rounded-full border-[0.5px] border-solid border-black/15 object-cover"
      />
    );
  }

  return (
    <span
      aria-hidden
      className="flex h-5 w-5 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border-[0.5px] border-solid border-black/15 text-[10px] font-semibold text-[#525252]"
      style={{ backgroundColor: owner.avatarBackground || '#F5F5F4' }}
    >
      {name.slice(0, 1).toUpperCase()}
    </span>
  );
}

function AccountMenu({
  platform,
  includedPlacementIds,
  mobile,
  onBack,
  onToggleAccount,
  selectablePlacementIds
}: {
  platform: CreatorAccountPickerPlatform;
  includedPlacementIds: ReadonlySet<string>;
  mobile: boolean;
  onBack: () => void;
  onToggleAccount: (account: CreatorAccountPickerAccount) => void;
  selectablePlacementIds: ReadonlySet<string>;
}) {
  const { t } = useTranslation();

  return (
    <div
      className={`w-[240px] overflow-hidden rounded-lg border border-solid border-black/10 bg-white ${MENU_SHADOW}`}
      role="menu"
      aria-label={getPlatformName(platform.platform)}
    >
      {mobile ? (
        <button
          type="button"
          className="flex h-[38px] w-full items-center gap-2 border-0 border-b border-solid border-[#E5E5E5] bg-white px-4 text-left text-sm font-semibold leading-5 text-[#404040]"
          onClick={onBack}
        >
          <MenuChevron backwards />
          <span className="min-w-0 flex-1 truncate">
            {getPlatformName(platform.platform)}
          </span>
        </button>
      ) : null}

      <div className="py-1">
        {platform.accounts.map(({ id, owner, account }) => {
          const { checked, indeterminate } = getCreatorAccountSelectionState(
            account,
            includedPlacementIds
          );
          const boundAndSelectable = account.verified;
          const accountContent = (
            <>
              <span
                className={`flex min-w-0 flex-1 items-center gap-2 ${
                  boundAndSelectable ? '' : 'opacity-50'
                }`}
              >
                <AccountAvatar account={account} owner={owner} />
                <span className="min-w-0 flex-1 truncate text-sm font-semibold leading-5 text-[#404040]">
                  {account.userName || owner.name}
                </span>
              </span>
              {boundAndSelectable ? (
                <CheckMark checked={checked} indeterminate={indeterminate} />
              ) : (
                <span className="flex flex-shrink-0 items-center gap-1 text-sm font-semibold leading-5 text-[#FF621D]">
                  <span>
                    {t('multi_platform.quote.verify_account', {
                      defaultValue: 'Verify'
                    })}
                  </span>
                  <VerifyArrow />
                </span>
              )}
            </>
          );

          return (
            <div key={id} className="flex h-[38px] items-center px-1.5 py-px">
              {boundAndSelectable ? (
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={
                    checked ? true : indeterminate ? 'mixed' : false
                  }
                  disabled={
                    !account.placementIds.some((id) =>
                      selectablePlacementIds.has(id)
                    ) &&
                    !checked &&
                    !indeterminate
                  }
                  className="flex h-9 w-full items-center gap-1 overflow-hidden rounded-md border-0 bg-white py-2 pl-2.5 pr-1.5 text-left hover:bg-[#FAFAFA] focus-visible:bg-[#FAFAFA] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => onToggleAccount(account)}
                >
                  {accountContent}
                </button>
              ) : (
                <a
                  href="/settings#connectSocials"
                  role="menuitem"
                  className="flex h-9 w-full items-center gap-1 overflow-hidden rounded-md py-2 pl-2.5 pr-1.5 text-left no-underline hover:bg-[#FAFAFA] focus-visible:bg-[#FAFAFA] focus-visible:outline-none"
                >
                  {accountContent}
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PlatformMenu({
  platforms,
  activePlatformId,
  includedPlacementIds,
  onSelectPlatform,
  onTogglePlatform,
  selectablePlacementIds
}: {
  platforms: CreatorAccountPickerPlatform[];
  activePlatformId?: string;
  includedPlacementIds: ReadonlySet<string>;
  onSelectPlatform: (platformId: string) => void;
  onTogglePlatform: (platform: CreatorAccountPickerPlatform) => void;
  selectablePlacementIds: ReadonlySet<string>;
}) {
  const { t } = useTranslation();

  return (
    <div
      className={`w-[240px] overflow-hidden rounded-lg border border-solid border-black/10 bg-white ${MENU_SHADOW}`}
      role="menu"
      aria-label={t('multi_platform.quote.select_account', {
        defaultValue: 'Select account'
      })}
    >
      <div className="border-b border-solid border-[#E5E5E5] py-1">
        {platforms.map((platform) => {
          const selectedCount = platform.accounts.filter(
            ({ account }) =>
              getCreatorAccountSelectionState(account, includedPlacementIds)
                .selected
          ).length;
          const checked =
            platform.accounts.length > 0 &&
            selectedCount === platform.accounts.length;
          const indeterminate = selectedCount > 0 && !checked;
          const active = platform.id === activePlatformId;
          const platformLabel = getPlatformName(platform.platform);

          return (
            <div
              key={platform.id}
              className="flex h-[38px] items-center px-1.5 py-px"
            >
              <div
                className={`flex h-9 w-full items-center gap-1 overflow-hidden rounded-md px-1.5 py-2 ${
                  active ? 'bg-[#FAFAFA]' : 'bg-white hover:bg-[#FAFAFA]'
                }`}
              >
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={
                    checked ? true : indeterminate ? 'mixed' : false
                  }
                  aria-label={`${platformLabel} ${selectedCount}/${platform.accounts.length}`}
                  disabled={
                    !platform.accounts.some(
                      ({ account }) =>
                        account.verified &&
                        account.placementIds.some(
                          (id) =>
                            selectablePlacementIds.has(id) ||
                            includedPlacementIds.has(id)
                        )
                    )
                  }
                  className="flex h-4 w-4 flex-shrink-0 items-center justify-center border-0 bg-transparent p-0"
                  onClick={() => onTogglePlatform(platform)}
                >
                  <CheckMark checked={checked} indeterminate={indeterminate} />
                </button>
                <button
                  type="button"
                  role="menuitem"
                  aria-haspopup="menu"
                  aria-expanded={active}
                  className="flex min-w-0 flex-1 items-center gap-1 border-0 bg-transparent p-0 text-left"
                  onClick={() => onSelectPlatform(platform.id)}
                >
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold leading-5 text-[#404040]">
                    {platformLabel}
                  </span>
                  <span className="whitespace-nowrap pr-1 text-xs font-medium leading-[18px] text-[#737373]">
                    {selectedCount}/{platform.accounts.length}
                  </span>
                  <MenuChevron />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <a
        href="/settings#connectSocials"
        role="menuitem"
        className="flex h-9 items-center gap-1 px-4 text-sm font-semibold leading-5 text-[#FF621D] no-underline hover:bg-[#FAFAFA] focus-visible:bg-[#FAFAFA] focus-visible:outline-none"
      >
        <AddIcon />
        {t('multi_platform.quote.add_account', {
          defaultValue: 'Add account'
        })}
      </a>
    </div>
  );
}

export default function CreatorAccountCascadePicker({
  collaborationPackage,
  includedPlacementIds,
  mobile = false,
  onIncludedPlacementIdsChange
}: {
  collaborationPackage: CreatorCollaborationPackage;
  includedPlacementIds: string[];
  mobile?: boolean;
  onIncludedPlacementIdsChange: (placementIds: string[]) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [activePlatformId, setActivePlatformId] = useState<string>();
  const [mobileAccountsOpen, setMobileAccountsOpen] = useState(false);
  const owners = useMemo(
    () => getCreatorAccountPickerOwners(collaborationPackage),
    [collaborationPackage]
  );
  const platforms = useMemo(
    () => getCreatorAccountPickerPlatforms(owners),
    [owners]
  );
  const includedSet = useMemo(
    () => new Set(includedPlacementIds),
    [includedPlacementIds]
  );
  const activePlatform =
    platforms.find(({ id }) => id === activePlatformId) ?? platforms[0];
  const placementOrder = collaborationPackage.placements.map(({ id }) => id);
  const selectablePlacementIds = new Set(
    getCreatorQuoteSelectablePlacementIds(collaborationPackage)
  );

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setMobileAccountsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        setMobileAccountsOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const toggleAccount = (account: CreatorAccountPickerAccount) => {
    onIncludedPlacementIdsChange(
      toggleCreatorAccountPlacementIds({
        account: {
          ...account,
          placementIds: account.placementIds.filter(
            (id) => selectablePlacementIds.has(id) || includedSet.has(id)
          )
        },
        includedPlacementIds,
        placementOrder
      })
    );
  };

  const togglePlatform = (platform: CreatorAccountPickerPlatform) => {
    onIncludedPlacementIdsChange(
      toggleCreatorAccountPlatformPlacementIds({
        platform: {
          ...platform,
          accounts: platform.accounts.map((item) => ({
            ...item,
            account: {
              ...item.account,
              placementIds: item.account.placementIds.filter(
                (id) => selectablePlacementIds.has(id) || includedSet.has(id)
              )
            }
          }))
        },
        includedPlacementIds,
        placementOrder
      })
    );
  };

  const selectPlatform = (platformId: string, showMobileAccounts = false) => {
    setActivePlatformId(platformId);
    if (showMobileAccounts) setMobileAccountsOpen(true);
  };

  return (
    <div ref={rootRef} className="relative max-md:w-full">
      <CreatorAddAccountsButton
        mobile={mobile}
        menuOpen={open}
        onClick={() => {
          setOpen((visible) => !visible);
          setActivePlatformId(platforms[0]?.id);
          setMobileAccountsOpen(false);
        }}
      />

      {open ? (
        <>
          <div className="absolute right-0 top-10 z-50 hidden items-start md:flex">
            <PlatformMenu
              platforms={platforms}
              activePlatformId={activePlatform?.id}
              includedPlacementIds={includedSet}
              onSelectPlatform={selectPlatform}
              onTogglePlatform={togglePlatform}
              selectablePlacementIds={selectablePlacementIds}
            />
            {activePlatform ? (
              <div className="mt-px">
                <AccountMenu
                  platform={activePlatform}
                  includedPlacementIds={includedSet}
                  mobile={false}
                  onBack={() => undefined}
                  onToggleAccount={toggleAccount}
                  selectablePlacementIds={selectablePlacementIds}
                />
              </div>
            ) : null}
          </div>

          <div className="absolute right-0 top-10 z-50 md:hidden">
            {mobileAccountsOpen && activePlatform ? (
              <AccountMenu
                platform={activePlatform}
                includedPlacementIds={includedSet}
                mobile
                onBack={() => setMobileAccountsOpen(false)}
                onToggleAccount={toggleAccount}
                selectablePlacementIds={selectablePlacementIds}
              />
            ) : (
              <PlatformMenu
                platforms={platforms}
                activePlatformId={activePlatform?.id}
                includedPlacementIds={includedSet}
                onSelectPlatform={(platformId) =>
                  selectPlatform(platformId, true)
                }
                onTogglePlatform={togglePlatform}
                selectablePlacementIds={selectablePlacementIds}
              />
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
